import { Box, LoadingOverlay, Modal } from '@mantine/core'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useCreateApiKey,
  useInvalidateWorkspaceApiKeys
} from '@/queries/workspaceQueries'
import { showToast } from '@/theme/notifications'
import { WorkspaceApiKeyScope } from '@/types/workspace'
import { ApiKeyCreatedView } from './ApiKeyCreatedView'
import { CreateApiKeyForm, SubmitValues } from './CreateApiKeyForm'

type CreatedKeyData = {
  key: string
  scope: WorkspaceApiKeyScope
}

type CreateApiKeyModalProps = {
  opened: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const CreateApiKeyModal = ({
  opened,
  onClose,
  onSuccess
}: CreateApiKeyModalProps) => {
  const intl = useIntl()
  const [createdKeyData, setCreatedKeyData] = useState<CreatedKeyData | null>(
    null
  )

  const { mutateAsync: createApiKey, isPending } = useCreateApiKey()
  const { invalidateWorkspaceApiKeys } = useInvalidateWorkspaceApiKeys()

  const handleSubmit = async (values: SubmitValues) => {
    try {
      const apiKey = await createApiKey({
        name: values.name,
        scope: values.scope,
        application_ids: values.applicationIds
      })

      setCreatedKeyData({ key: apiKey.key, scope: values.scope })
      void invalidateWorkspaceApiKeys()
      onSuccess?.()
    } catch (err) {
      const fallback = intl.formatMessage({
        id: 'workspace.apiKeys.create.error'
      })
      showToast(extractErrorMessage(err, fallback), 'error')
    }
  }

  const resetModalState = () => {
    setCreatedKeyData(null)
  }

  return (
    <Modal
      size="xl"
      opened={opened}
      title={
        createdKeyData ? (
          <FormattedMessage id="workspace.apiKeys.created.title" />
        ) : (
          <FormattedMessage id="workspace.apiKeys.create" />
        )
      }
      onClose={onClose}
      onExitTransitionEnd={resetModalState}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        {createdKeyData ? (
          <ApiKeyCreatedView
            keyValue={createdKeyData.key}
            scope={createdKeyData.scope}
            onClose={onClose}
          />
        ) : (
          <CreateApiKeyForm
            onCancel={onClose}
            onSubmit={(values) => void handleSubmit(values)}
          />
        )}
      </Box>
    </Modal>
  )
}
